import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';

const fmt = (v, type) => {
  if (v == null) return '—';
  if (type === 'montant') return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(v);
  return `${(v * 100).toFixed(1)} %`;
};

const INDICATEURS = [
  { key: 'chiffre_affaires',       label: "Chiffre d'affaires",     type: 'montant' },
  { key: 'ebe',                    label: 'EBE',                    type: 'montant' },
  { key: 'taux_ebe',               label: 'EBE / CA',               type: 'ratio' },
  { key: 'resultat_net',           label: 'Résultat net',           type: 'montant' },
  { key: 'taux_resultat_net',      label: 'Résultat net / CA',      type: 'ratio' },
  { key: 'caf',                    label: 'CAF',                    type: 'montant' },
  { key: 'ratio_masse_salariale',  label: 'Masse salariale / CA',   type: 'ratio' },
  { key: 'ratio_charges_externes', label: 'Charges ext. / CA',      type: 'ratio' },
  { key: 'fonds_propres',          label: 'Fonds propres',          type: 'montant' },
  { key: 'ratio_endettement',      label: "Ratio d'endettement",    type: 'ratio' },
  { key: 'tresorerie_nette',       label: 'Trésorerie nette',       type: 'montant' },
  { key: 'capacite_remboursement', label: 'Capacité rembours.',     type: 'ratio' },
];

const LOWER_BETTER = ['ratio_endettement', 'ratio_masse_salariale', 'ratio_charges_externes', 'ratio_achats', 'capacite_remboursement'];

export default function Classements() {
  const { user } = useAuth();
  const [tab, setTab]               = useState('classement'); // classement | comparatif
  const [exercice, setExercice]     = useState(new Date().getFullYear() - 1);
  const [tri, setTri]               = useState('chiffre_affaires');
  const [ordre, setOrdre]           = useState('desc');
  const [regionFiltreC, setRegionFiltreC] = useState('');
  const [trancheFiltre, setTrancheFiltre] = useState('');
  const [tranches, setTranches]     = useState([]);
  const [regions, setRegions]       = useState([]);
  const [adherents, setAdherents]   = useState([]);
  const [selectedAdherent, setSelectedAdherent] = useState('');
  const [classement, setClassement] = useState(null);
  const [comparatif, setComparatif] = useState(null);
  const [loading, setLoading]       = useState(false);

  useEffect(() => {
    api.get('/configuration/tranches').then(r => setTranches(r.data)).catch(() => {});
    api.get('/regions/').then(r => setRegions(r.data)).catch(() => {});
    if (['ADMIN', 'GESTIONNAIRE'].includes(user.role)) {
      api.get('/adherents/').then(r => setAdherents(r.data)).catch(() => {});
    }
  }, []);

  const loadClassement = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ exercice, tri, ordre });
      if (regionFiltreC) params.append('region_ecf', regionFiltreC);
      if (trancheFiltre) params.append('tranche_ca_id', trancheFiltre);
      const { data } = await api.get(`/classements/classement?${params}`);
      setClassement(data);
    } catch (e) {} finally { setLoading(false); }
  }, [exercice, tri, ordre, regionFiltreC, trancheFiltre]);

  const loadComparatif = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ exercice });
      if (selectedAdherent) params.append('adherent_id', selectedAdherent);
      const { data } = await api.get(`/classements/comparatif?${params}`);
      setComparatif(data);
    } catch (e) {} finally { setLoading(false); }
  }, [exercice, selectedAdherent]);

  useEffect(() => {
    if (tab === 'classement') loadClassement();
    else loadComparatif();
  }, [tab, loadClassement, loadComparatif]);

  const triLabel = INDICATEURS.find(i => i.key === tri)?.label || tri;
  const triType  = INDICATEURS.find(i => i.key === tri)?.type || 'montant';

  return (
    <div>
      <div className="topbar">
        <div className="topbar-title">Classements & Comparatifs</div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <label style={{ fontSize: '0.72rem', color: 'var(--ecf-grey)', fontWeight: 700, letterSpacing: 1 }}>EXERCICE</label>
          <input type="number" className="form-input" value={exercice} min="2000" max="2099"
            onChange={e => setExercice(parseInt(e.target.value))}
            style={{ margin: 0, width: 90, fontSize: '0.85rem' }} />
        </div>
      </div>

      <div className="page-content">

        {/* Onglets */}
        <div style={{ display: 'flex', gap: 0, marginBottom: 20, borderBottom: '2px solid var(--ecf-mid-grey)' }}>
          {[
            ['classement', '🏆 Classements'],
            ['comparatif',  '📊 Comparatifs Région / National'],
          ].map(([key, label]) => (
            <button key={key} onClick={() => setTab(key)} style={{
              padding: '10px 20px', border: 'none', cursor: 'pointer', background: 'none',
              fontFamily: 'var(--font)', fontSize: '0.82rem', fontWeight: 700,
              color: tab === key ? 'var(--ecf-blue)' : 'var(--ecf-grey)',
              borderBottom: tab === key ? '2px solid var(--ecf-blue)' : '2px solid transparent',
              marginBottom: -2,
            }}>{label}</button>
          ))}
        </div>

        {/* ── TAB CLASSEMENTS ── */}
        {tab === 'classement' && (
          <>
            {/* Filtres */}
            <div className="card" style={{ padding: '16px 20px' }}>
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                <div>
                  <div className="form-label" style={{ marginBottom: 4 }}>Trier par</div>
                  <select className="form-input" value={tri} onChange={e => setTri(e.target.value)} style={{ margin: 0, width: 200 }}>
                    {INDICATEURS.map(i => <option key={i.key} value={i.key}>{i.label}</option>)}
                  </select>
                </div>
                <div>
                  <div className="form-label" style={{ marginBottom: 4 }}>Ordre</div>
                  <select className="form-input" value={ordre} onChange={e => setOrdre(e.target.value)} style={{ margin: 0, width: 130 }}>
                    <option value="desc">Décroissant</option>
                    <option value="asc">Croissant</option>
                  </select>
                </div>
                <div>
                  <div className="form-label" style={{ marginBottom: 4 }}>Région</div>
                  <select className="form-input" value={regionFiltreC} onChange={e => setRegionFiltreC(e.target.value)} style={{ margin: 0, width: 220 }}>
                    <option value="">Toutes les régions</option>
                    {regions.map(r => <option key={r.id} value={r.libelle}>RÉGION {r.numero} — {r.libelle}</option>)}
                  </select>
                </div>
                <div>
                  <div className="form-label" style={{ marginBottom: 4 }}>Tranche CA</div>
                  <select className="form-input" value={trancheFiltre} onChange={e => setTrancheFiltre(e.target.value)} style={{ margin: 0, width: 180 }}>
                    <option value="">Toutes les tranches</option>
                    {tranches.filter(t => t.actif).map(t => <option key={t.id} value={t.id}>{t.libelle}</option>)}
                  </select>
                </div>
                <button className="btn btn-primary" onClick={loadClassement} style={{ marginBottom: 0 }}>
                  Actualiser
                </button>
              </div>
            </div>

            {/* Résultats */}
            {loading ? (
              <div style={{ padding: 32, textAlign: 'center', color: '#999' }}>Chargement...</div>
            ) : classement ? (
              <div className="card" style={{ padding: 0 }}>
                <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--ecf-mid-grey)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div className="card-title" style={{ margin: 0 }}>
                    Classement par {triLabel} — {classement.total} adhérent(s)
                    {regionFiltreC && <span style={{ marginLeft: 8, fontSize: '0.72rem', color: 'var(--ecf-blue)' }}>• {regionFiltreC}</span>}
                  </div>
                </div>
                <div className="table-wrapper">
                  <table>
                    <thead>
                      <tr>
                        <th style={{ width: 60 }}>Rang</th>
                        <th>Adhérent</th>
                        <th>Région</th>
                        {INDICATEURS.map(i => (
                          <th key={i.key} style={{ textAlign: 'right', background: i.key === tri ? 'rgba(218,31,38,0.15)' : '' }}>
                            {i.label}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {classement.classement.map((row, idx) => {
                        const val = row.indicateurs[tri];
                        const isMe = row.adherent_id != null;
                        return (
                          <tr key={idx} style={{ background: idx === 0 ? 'rgba(255,215,0,0.06)' : isMe && user.role === 'ADHERENT' ? 'rgba(18,68,131,0.04)' : '' }}>
                            <td style={{ textAlign: 'center', fontWeight: 900, color: idx === 0 ? '#DAA520' : idx === 1 ? '#999' : idx === 2 ? '#cd7f32' : 'var(--ecf-grey)' }}>
                              {idx === 0 ? '🥇' : idx === 1 ? '🥈' : idx === 2 ? '🥉' : row.rang}
                            </td>
                            <td className="important">{row.nom_commercial}</td>
                            <td style={{ fontSize: '0.78rem', color: 'var(--ecf-grey)' }}>{row.region_ecf || '—'}</td>
                            {INDICATEURS.map(i => {
                              const v = row.indicateurs[i.key];
                              const isTriCol = i.key === tri;
                              return (
                                <td key={i.key} style={{ textAlign: 'right', fontWeight: isTriCol ? 700 : 400, color: isTriCol ? 'var(--ecf-blue)' : 'var(--ecf-grey)', background: isTriCol ? 'rgba(18,68,131,0.03)' : '' }}>
                                  {fmt(v, i.type)}
                                </td>
                              );
                            })}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : null}
          </>
        )}

        {/* ── TAB COMPARATIFS ── */}
        {tab === 'comparatif' && (
          <>
            <div className="card" style={{ padding: '16px 20px' }}>
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                {['ADMIN', 'GESTIONNAIRE'].includes(user.role) && (
                  <div>
                    <div className="form-label" style={{ marginBottom: 4 }}>Adhérent</div>
                    <select className="form-input" value={selectedAdherent}
                      onChange={e => setSelectedAdherent(e.target.value)} style={{ margin: 0, width: 240 }}>
                      <option value="">— Sélectionner —</option>
                      {adherents.map(a => <option key={a.id} value={a.id}>{a.nom_commercial}</option>)}
                    </select>
                  </div>
                )}
                <button className="btn btn-primary" onClick={loadComparatif}>Actualiser</button>
              </div>
            </div>

            {loading ? (
              <div style={{ padding: 32, textAlign: 'center', color: '#999' }}>Chargement...</div>
            ) : comparatif ? (
              <>
                {/* Infos contexte */}
                <div style={{ display: 'flex', gap: 12, marginBottom: 18 }}>
                  <div className="kpi-card" style={{ flex: 1, borderTopColor: 'var(--ecf-blue)' }}>
                    <div className="kpi-label">Périmètre national</div>
                    <div className="kpi-value">{comparatif.nb_national}</div>
                    <div style={{ fontSize: '0.72rem', color: 'var(--ecf-grey)', marginTop: 4 }}>adhérents avec bilan validé</div>
                  </div>
                  <div className="kpi-card" style={{ flex: 1, borderTopColor: 'var(--ecf-red)' }}>
                    <div className="kpi-label">Périmètre régional</div>
                    <div className="kpi-value">{comparatif.nb_regional}</div>
                    <div style={{ fontSize: '0.72rem', color: 'var(--ecf-grey)', marginTop: 4 }}>{comparatif.region_ecf || '—'}</div>
                  </div>
                </div>

                {/* Tableau comparatif */}
                <div className="card" style={{ padding: 0 }}>
                  <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--ecf-mid-grey)' }}>
                    <div className="card-title" style={{ margin: 0 }}>
                      Comparatif indicateurs — Exercice {exercice}
                    </div>
                  </div>
                  <div className="table-wrapper">
                    <table>
                      <thead>
                        <tr>
                          <th>Indicateur</th>
                          <th style={{ textAlign: 'right', background: 'rgba(18,68,131,0.1)' }}>Vous / Adhérent</th>
                          <th style={{ textAlign: 'right', background: 'rgba(218,31,38,0.08)' }}>Médiane Région</th>
                          <th style={{ textAlign: 'right' }}>Q1 Région</th>
                          <th style={{ textAlign: 'right' }}>Q3 Région</th>
                          <th style={{ textAlign: 'right', background: 'rgba(0,0,0,0.04)' }}>Médiane National</th>
                          <th style={{ textAlign: 'right' }}>Q1 National</th>
                          <th style={{ textAlign: 'right' }}>Q3 National</th>
                          <th style={{ textAlign: 'center' }}>Position</th>
                        </tr>
                      </thead>
                      <tbody>
                        {INDICATEURS.map(ind => {
                          const data = comparatif.comparatif[ind.key];
                          if (!data) return null;
                          const moi = data.moi;
                          const medNat = data.national?.mediane;
                          const medReg = data.regional?.mediane;
                          const lowerBetter = LOWER_BETTER.includes(ind.key);

                          // Position vs national
                          let posNat = null;
                          if (moi != null && medNat != null) {
                            const better = lowerBetter ? moi < medNat : moi > medNat;
                            posNat = better ? 'above' : 'below';
                          }
                          // Position vs région
                          let posReg = null;
                          if (moi != null && medReg != null) {
                            const better = lowerBetter ? moi < medReg : moi > medReg;
                            posReg = better ? 'above' : 'below';
                          }

                          return (
                            <tr key={ind.key}>
                              <td style={{ fontWeight: 600, color: 'var(--ecf-black)', fontSize: '0.82rem' }}>{ind.label}</td>
                              <td style={{ textAlign: 'right', fontWeight: 700, color: 'var(--ecf-blue)', background: 'rgba(18,68,131,0.04)' }}>
                                {fmt(moi, ind.type)}
                              </td>
                              <td style={{ textAlign: 'right', fontWeight: 600, background: 'rgba(218,31,38,0.04)' }}>
                                {fmt(data.regional?.mediane, ind.type)}
                              </td>
                              <td style={{ textAlign: 'right', color: 'var(--ecf-grey)', fontSize: '0.78rem' }}>
                                {fmt(data.regional?.q1, ind.type)}
                              </td>
                              <td style={{ textAlign: 'right', color: 'var(--ecf-grey)', fontSize: '0.78rem' }}>
                                {fmt(data.regional?.q3, ind.type)}
                              </td>
                              <td style={{ textAlign: 'right', fontWeight: 600, background: 'rgba(0,0,0,0.02)' }}>
                                {fmt(data.national?.mediane, ind.type)}
                              </td>
                              <td style={{ textAlign: 'right', color: 'var(--ecf-grey)', fontSize: '0.78rem' }}>
                                {fmt(data.national?.q1, ind.type)}
                              </td>
                              <td style={{ textAlign: 'right', color: 'var(--ecf-grey)', fontSize: '0.78rem' }}>
                                {fmt(data.national?.q3, ind.type)}
                              </td>
                              <td style={{ textAlign: 'center' }}>
                                <div style={{ display: 'flex', gap: 4, justifyContent: 'center' }}>
                                  {posReg && (
                                    <span title="vs Région" style={{ fontSize: '0.75rem', fontWeight: 700, color: posReg === 'above' ? 'var(--ecf-success)' : 'var(--ecf-error)' }}>
                                      {posReg === 'above' ? '▲R' : '▼R'}
                                    </span>
                                  )}
                                  {posNat && (
                                    <span title="vs National" style={{ fontSize: '0.75rem', fontWeight: 700, color: posNat === 'above' ? 'var(--ecf-success)' : 'var(--ecf-error)' }}>
                                      {posNat === 'above' ? '▲N' : '▼N'}
                                    </span>
                                  )}
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  <div style={{ padding: '10px 20px', background: 'var(--ecf-light-grey)', fontSize: '0.72rem', color: 'var(--ecf-grey)' }}>
                    ▲R = au-dessus de la médiane régionale &nbsp;|&nbsp; ▲N = au-dessus de la médiane nationale &nbsp;|&nbsp;
                    Pour les ratios de coûts (masse salariale, endettement...) ▲ = meilleur que la médiane
                  </div>
                </div>
              </>
            ) : (
              <div className="card" style={{ textAlign: 'center', padding: 32, color: '#999' }}>
                {['ADMIN', 'GESTIONNAIRE'].includes(user.role)
                  ? 'Sélectionnez un adhérent pour afficher le comparatif'
                  : 'Aucune donnée disponible pour cet exercice'}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
